// Shared date formatting. Each helper preserves the exact output of the
// per-file helpers it replaced — pick the variant that matches the existing
// UI, don't introduce a new format ad hoc.

export type DateInput = Date | string | null | undefined;

const toDate = (value: Date | string): Date =>
  typeof value === 'string' ? new Date(value) : value;

/** 'Jul 2, 2026' */
export function formatDate(value: DateInput, fallback = ''): string {
  if (!value) return fallback;
  return toDate(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** '7/2/2026' (viewer's locale) */
export function formatDateNumeric(value: DateInput, fallback = ''): string {
  if (!value) return fallback;
  return toDate(value).toLocaleDateString();
}

/** '7/2/2026, 1:23:45 AM' (viewer's locale) */
export function formatDateTime(value: DateInput, fallback = ''): string {
  if (!value) return fallback;
  return toDate(value).toLocaleString();
}

/** 'Jul 2, 2026, 01:23 AM' */
export function formatDateTimeLong(value: DateInput, fallback = ''): string {
  if (!value) return fallback;
  return toDate(value).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** 'Just now' / '5m ago' / '3h ago' / '2d ago', then 'Jul 2' */
export function formatRelativeTime(value: DateInput, fallback = ''): string {
  if (!value) return fallback;
  const date = toDate(value);
  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** 'Today' / 'Yesterday' / '3 days ago' / '2 weeks ago' / '4 months ago' / '2 years ago' */
export function formatRelativeDays(value: DateInput, fallback = ''): string {
  if (!value) return fallback;
  const date = toDate(value);
  const diffDays = Math.floor((Date.now() - date.getTime()) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
}

/**
 * Calendar dates (permit expiration, estimated start/completion) come from the
 * API as midnight-UTC datetimes; parsing them with `new Date()` rolls them back
 * a day for anyone west of Greenwich ("I set 3/5/28 and it shows 3/4/28",
 * Jim 2026-09-15). Read the YYYY-MM-DD prefix and build a local date instead.
 */
export function parseDateOnly(value: DateInput): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return new Date(value);
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** '3/5/2028' for a calendar date, without the timezone day-shift. */
export function formatDateOnlyNumeric(value: DateInput, fallback = ''): string {
  const d = parseDateOnly(value);
  return d ? d.toLocaleDateString() : fallback;
}

/** 'YYYY-MM-DD' for an <input type="date"> value, same rule. */
export function dateOnlyInputValue(value: DateInput): string {
  const d = parseDateOnly(value);
  if (!d) return '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/**
 * True when a calendar date falls before today (local). Comparing the
 * midnight-UTC value as an instant flags "today" as passed all day and
 * "tomorrow" as passed from 7 pm Central — which is how a date saved in the
 * est-completion gate came straight back on the next visit (Shane's Agave,
 * 2026-09-15).
 */
export function isDateOnlyPast(
  value: DateInput,
  now: Date = new Date()
): boolean {
  const d = parseDateOnly(value);
  if (!d) return false;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return d < today;
}
